import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ArrowLeft, AlertCircle, Loader2, FileCheck } from "lucide-react";
import { useWorkspaceMisstatements } from "../../hooks/useWorkspaceMisstatements";
import type { WorkspaceMisstatement } from "../../hooks/useWorkspaceMisstatements";
import MisstatementDetailView from "../misstatements/MisstatementDetailView";
import { useAuth } from "@/contexts/AuthContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { apiService } from "@/services/api-service";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Checkbox } from "../ui/checkbox";

type ControlSignoffSection = "prep_so" | "rev_so" | "conc_so" | "eqr_so" | "partner_so";

interface WorkspaceMisstatementsProps {
  workspaceName: string;
  onBack?: () => void;
}

function getInitialsForSignoff(name: string): string {
  if (!name) return "";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function ControlSignoffBadge({
  label,
  signoffArray,
  onSignoffClick,
  disabled,
}: {
  label: string;
  signoffArray: Array<{ name: string; metadata: string }>;
  onSignoffClick: () => void;
  disabled: boolean;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const validEntries = (signoffArray || []).filter((e) => e?.name && String(e.name).trim() !== "");
  const hasEntries = validEntries.length > 0;
  const formatDate = (s: string) => {
    if (!s) return "";
    try { return new Date(s).toLocaleString(); } catch { return s; }
  };
  return (
    <div className="relative inline-flex items-center gap-1">
      {hasEntries && (
        <div
          className="flex items-center gap-0.5"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {validEntries.slice(0, 3).map((entry, idx) => (
            <div
              key={idx}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium cursor-help border border-blue-500/30"
              title={`${entry.name} - ${formatDate(entry.metadata || "")}`}
            >
              {getInitialsForSignoff(entry.name)}
            </div>
          ))}
          {validEntries.length > 3 && (
            <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
              +{validEntries.length - 3}
            </div>
          )}
        </div>
      )}
      <Button
        size="sm"
        variant="ghost"
        onClick={onSignoffClick}
        disabled={disabled}
        className={`h-6 w-6 p-0 ${disabled ? "text-white/30 cursor-not-allowed" : "text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"}`}
        title={disabled ? `You don't have permission to add ${label} signoff` : `Add ${label} signoff`}
      >
        <FileCheck className="h-3 w-3" />
      </Button>
      {showTooltip && hasEntries && (
        <div className="absolute z-10 bottom-full left-1/2 mb-2 min-w-[160px] max-w-xs -translate-x-1/2 rounded border border-white/10 bg-gray-900 px-2 py-1 text-xs text-white shadow-lg">
          <div className="font-semibold mb-1">{label} ({validEntries.length})</div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {validEntries.map((entry, idx) => (
              <div key={idx} className="border-b border-white/10 pb-1 last:border-b-0 last:pb-0">
                <div className="font-medium">{entry.name}</div>
                {entry.metadata && <div className="text-white/70 text-[10px]">{formatDate(entry.metadata)}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function WorkspaceMisstatements({
  workspaceName,
  onBack,
}: WorkspaceMisstatementsProps) {
  const { isArchivalMode } = useArchivalMode();
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();

  const {
    factual,
    judgemental,
    projected,
    loading,
    error,
    hasMisstatements,
    loadWorkspaceMisstatements,
  } = useWorkspaceMisstatements(workspaceName);

  const [misstatementSignoffLoading, setMisstatementSignoffLoading] = useState(false);
  const [selectedMisstatementsForSignoff, setSelectedMisstatementsForSignoff] = useState<Set<string>>(new Set());
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);

  const [activeType, setActiveType] = useState<
    "Factual" | "Judgemental" | "Projected"
  >("Factual");
  const [selectedMisstatement, setSelectedMisstatement] = useState<{
    id: string;
    data: any;
  } | null>(null);

  useEffect(() => {
    const loadUserEngagementRole = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const currentUserMember = team.find((member: any) => String(member.id) === String(user.id));
        setUserEngagementRole(currentUserMember?.role ?? null);
      } catch (err) {
        console.error("Error loading user engagement role for misstatement signoff:", err);
        setUserEngagementRole(null);
      }
    };
    loadUserEngagementRole();
  }, [currentEngagement, user]);

  const canControlSignoffSection = (section: ControlSignoffSection): boolean => {
    if (!user) return false;
    const engagementRole = userEngagementRole;
    const userType = user.type;
    switch (section) {
      case "prep_so":
        return (
          engagementRole === "associate" ||
          engagementRole === "article" ||
          userType === "associate" ||
          userType === "article"
        );
      case "rev_so":
        return (
          engagementRole === "engagement_manager" ||
          userType === "manager" ||
          userType === "associate" ||
          engagementRole === "associate"
        );
      case "conc_so":
        return userType === "manager" || userType === "partner";
      case "eqr_so":
        return engagementRole === "eqr_partner" || engagementRole === "eqr_manager";
      case "partner_so":
        return engagementRole === "engagement_partner";
      default:
        return false;
    }
  };

  const isMisstatementPrepCompleted = (signoff: WorkspaceMisstatement["signoff"]): boolean => {
    if (!signoff || !Array.isArray(signoff.prep_so)) return false;
    return signoff.prep_so.some((entry) => entry?.name && String(entry.name).trim() !== "");
  };

  const handleMisstatementSignoff = async (misstatementId: string, section: ControlSignoffSection) => {
    if (!user || !user.user_name) return;
    try {
      setMisstatementSignoffLoading(true);
      if (
        !(window as any).sharePointAPI?.downloadMisstatementJson ||
        !(window as any).sharePointAPI?.uploadMisstatementJson
      ) {
        alert("SharePoint Misstatement APIs are not available.");
        return;
      }
      const msResult = await (window as any).sharePointAPI.downloadMisstatementJson();
      if (!msResult?.success || !msResult.data) {
        alert(msResult?.error || "Failed to load Libraries_Misstatement.json");
        return;
      }
      const msData = msResult.data;
      if (!msData.Libraries_Misstatement) {
        msData.Libraries_Misstatement = { misstatements: {}, responses: [] };
      }
      if (!msData.Libraries_Misstatement.misstatements) {
        msData.Libraries_Misstatement.misstatements = {};
      }
      if (!msData.Libraries_Misstatement.misstatements[misstatementId]) {
        alert("Misstatement not found in Libraries_Misstatement.json");
        return;
      }
      const entry = msData.Libraries_Misstatement.misstatements[misstatementId];
      if (!entry.signoff) {
        entry.signoff = {
          prep_so: [],
          rev_so: [],
          conc_so: [],
          eqr_so: [],
          partner_so: [],
        };
      }
      const signoffArr: any[] = Array.isArray(entry.signoff[section]) ? entry.signoff[section] : [];
      signoffArr.push({ name: user.user_name, metadata: new Date().toISOString() });
      entry.signoff[section] = signoffArr;

      const uploadResult = await (window as any).sharePointAPI.uploadMisstatementJson(msData);
      if (!uploadResult?.success) {
        alert(uploadResult?.error || "Failed to save misstatement signoff");
        return;
      }
      await loadWorkspaceMisstatements();
    } catch (e: any) {
      console.error("Error updating misstatement signoff:", e);
      alert(e?.message || "Unexpected error while updating misstatement signoff");
    } finally {
      setMisstatementSignoffLoading(false);
    }
  };

  const handleBulkMisstatementSignoff = async (section: ControlSignoffSection) => {
    if (!user?.user_name || selectedMisstatementsForSignoff.size === 0) return;
    const ids = Array.from(selectedMisstatementsForSignoff);
    try {
      setMisstatementSignoffLoading(true);
      for (const id of ids) {
        await handleMisstatementSignoff(id, section);
      }
      setSelectedMisstatementsForSignoff(new Set());
    } finally {
      setMisstatementSignoffLoading(false);
    }
  };

  const getActiveMisstatements = () => {
    switch (activeType) {
      case "Factual":
        return factual;
      case "Judgemental":
        return judgemental;
      case "Projected":
        return projected;
      default:
        return [];
    }
  };

  const activeMisstatements = getActiveMisstatements();

  const handleSaveMisstatement = async (updatedData: any) => {
    try {
      if (!window.sharePointAPI?.updateMisstatement) {
        throw new Error("SharePoint API for updating misstatements is not available");
      }

      const result = await window.sharePointAPI.updateMisstatement({
        misstatementId: selectedMisstatement?.id,
        data: updatedData,
      });

      if (!result?.success) {
        throw new Error(result?.error || "Failed to update misstatement");
      }

      await loadWorkspaceMisstatements();
      setSelectedMisstatement(null);
    } catch (error) {
      console.error("Failed to save misstatement:", error);
      throw error;
    }
  };

  // If a misstatement is selected, show detail view
  if (selectedMisstatement) {
    return (
      <MisstatementDetailView
        misstatementId={selectedMisstatement.id}
        misstatementData={selectedMisstatement.data}
        workspaceName={workspaceName}
        onBack={() => setSelectedMisstatement(null)}
        onSave={handleSaveMisstatement}
        mode="view"
      />
    );
  }

  return (
    <div className="space-y-4 text-white">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          {onBack && (
            <Button
              variant="outline"
              onClick={onBack}
              className="border-white/10 text-white hover:bg-white/10"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          )}
          <div>
            <h2 className="text-xl font-bold">
              Misstatements – {workspaceName}
            </h2>
            <p className="text-sm text-white/60">
              Misstatements linked to this workspace.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 rounded-md border border-white/10 bg-white/5 p-1">
          <button
            onClick={() => setActiveType("Factual")}
            className={`rounded px-3 py-1.5 text-sm transition-colors ${
              activeType === "Factual"
                ? "bg-blue-500/20 text-blue-200"
                : "text-white/70 hover:bg-white/10 hover:text-white"
            }`}
          >
            Factual <Badge variant="outline" className="ml-1 text-[10px]">{factual.length}</Badge>
          </button>
          <button
            onClick={() => setActiveType("Judgemental")}
            className={`rounded px-3 py-1.5 text-sm transition-colors ${
              activeType === "Judgemental"
                ? "bg-blue-500/20 text-blue-200"
                : "text-white/70 hover:bg-white/10 hover:text-white"
            }`}
          >
            Judgemental <Badge variant="outline" className="ml-1 text-[10px]">{judgemental.length}</Badge>
          </button>
          <button
            onClick={() => setActiveType("Projected")}
            className={`rounded px-3 py-1.5 text-sm transition-colors ${
              activeType === "Projected"
                ? "bg-blue-500/20 text-blue-200"
                : "text-white/70 hover:bg-white/10 hover:text-white"
            }`}
          >
            Projected <Badge variant="outline" className="ml-1 text-[10px]">{projected.length}</Badge>
          </button>
        </div>
      </div>

      <div className="space-y-4">

        {loading ? (
          <Card className="border-white/10 bg-white/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-center gap-2 text-white/60">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading misstatements from SharePoint...
              </div>
            </CardContent>
          </Card>
        ) : error ? (
          <Card className="border-red-500/40 bg-red-500/10">
            <CardContent className="flex items-center gap-2 p-4 text-sm text-red-200">
              <AlertCircle className="h-4 w-4" />
              {error}
            </CardContent>
          </Card>
        ) : !hasMisstatements ? (
          <Card className="border-white/10 bg-white/5">
            <CardContent className="p-6 text-center text-white/60">
              <AlertCircle className="mx-auto mb-3 h-8 w-8 text-white/40" />
              <p className="text-sm">
                No misstatements are currently linked to this workspace.
              </p>
              <p className="mt-1 text-xs">
                Misstatements will appear here when they are created and linked
                to &quot;{workspaceName}&quot;.
              </p>
            </CardContent>
          </Card>
        ) : activeMisstatements.length === 0 ? (
          <Card className="border-white/10 bg-white/5">
            <CardContent className="p-6 text-center text-white/60">
              <AlertCircle className="mx-auto mb-3 h-8 w-8 text-white/40" />
              <p className="text-sm">
                No {activeType.toLowerCase()} misstatements found for this
                workspace.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="text-xs">
                {activeMisstatements.length} {activeType.toLowerCase()} misstatement(s)
              </Badge>
              {!isArchivalMode && selectedMisstatementsForSignoff.size > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={misstatementSignoffLoading}
                      className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10 hover:text-blue-200"
                    >
                      {misstatementSignoffLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FileCheck className="h-4 w-4 mr-2" />
                      )}
                      Sign off on selected ({selectedMisstatementsForSignoff.size})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-gray-900 border-white/10 text-white min-w-[200px]" align="end">
                    {(() => {
                      const selected = Array.from(selectedMisstatementsForSignoff);
                      const allHavePrep = selected.length > 0 && selected.every((id) => {
                        const m = activeMisstatements.find((x) => x.id === id);
                        return m && isMisstatementPrepCompleted(m.signoff || { prep_so: [], rev_so: [], conc_so: [], eqr_so: [], partner_so: [] });
                      });
                      const canPrep = canControlSignoffSection("prep_so");
                      const canRev = canControlSignoffSection("rev_so") && allHavePrep;
                      const canConc = canControlSignoffSection("conc_so") && allHavePrep;
                      const canEqr = canControlSignoffSection("eqr_so") && allHavePrep;
                      const canPartner = canControlSignoffSection("partner_so") && allHavePrep;
                      return (
                        <>
                          <DropdownMenuItem disabled={!canPrep} onClick={() => canPrep && handleBulkMisstatementSignoff("prep_so")}>Preparer</DropdownMenuItem>
                          <DropdownMenuItem disabled={!canRev} onClick={() => canRev && handleBulkMisstatementSignoff("rev_so")}>Reviewer</DropdownMenuItem>
                          <DropdownMenuItem disabled={!canConc} onClick={() => canConc && handleBulkMisstatementSignoff("conc_so")}>Concurrent</DropdownMenuItem>
                          <DropdownMenuItem disabled={!canEqr} onClick={() => canEqr && handleBulkMisstatementSignoff("eqr_so")}>EQR</DropdownMenuItem>
                          <DropdownMenuItem disabled={!canPartner} onClick={() => canPartner && handleBulkMisstatementSignoff("partner_so")}>Partner</DropdownMenuItem>
                        </>
                      );
                    })()}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {!misstatementSignoffLoading && (
              <div className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 px-3 py-2">
                {!isArchivalMode && (
                  <div className="w-10 shrink-0 flex items-center justify-center">
                    <Checkbox
                      checked={activeMisstatements.length > 0 && activeMisstatements.every((m) => selectedMisstatementsForSignoff.has(m.id))}
                      onCheckedChange={() => {
                        if (activeMisstatements.length > 0 && activeMisstatements.every((m) => selectedMisstatementsForSignoff.has(m.id))) {
                          setSelectedMisstatementsForSignoff(new Set());
                        } else {
                          setSelectedMisstatementsForSignoff(new Set(activeMisstatements.map((m) => m.id)));
                        }
                      }}
                      aria-label="Select all misstatements"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0 text-xs font-semibold text-white/70">
                  Misstatement
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="w-24 text-center text-xs font-semibold text-white/70" title="Preparer">Preparer</div>
                  <div className="w-24 text-center text-xs font-semibold text-white/70" title="Reviewer">Reviewer</div>
                  <div className="w-24 text-center text-xs font-semibold text-white/70" title="Concurrent">Concurrent</div>
                  <div className="w-24 text-center text-xs font-semibold text-white/70" title="EQR">EQR</div>
                  <div className="w-24 text-center text-xs font-semibold text-white/70" title="Partner">Partner</div>
                </div>
                <div className="w-8 flex-shrink-0" />
              </div>
            )}
            {activeMisstatements.map((mis) => {
              const signoff = mis.signoff || {
                prep_so: [],
                rev_so: [],
                conc_so: [],
                eqr_so: [],
                partner_so: [],
              };
              const prepCompleted = isMisstatementPrepCompleted(signoff);
              const canPrep = canControlSignoffSection("prep_so");
              const canRev = canControlSignoffSection("rev_so") && prepCompleted;
              const canConc = canControlSignoffSection("conc_so") && prepCompleted;
              const canEqr = canControlSignoffSection("eqr_so") && prepCompleted;
              const canPartner = canControlSignoffSection("partner_so") && prepCompleted;

              return (
                <div
                  key={mis.id}
                  className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 p-3 hover:border-white/20 hover:bg-white/10"
                >
                  {!isArchivalMode && (
                    <div className="w-10 shrink-0 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedMisstatementsForSignoff.has(mis.id)}
                        onCheckedChange={() => {
                          setSelectedMisstatementsForSignoff((prev) => {
                            const next = new Set(prev);
                            if (next.has(mis.id)) next.delete(mis.id);
                            else next.add(mis.id);
                            return next;
                          });
                        }}
                        aria-label={`Select ${mis.id}`}
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    className="flex-1 min-w-0 text-left"
                    onClick={() => setSelectedMisstatement({ id: mis.id, data: mis.data })}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-medium text-white truncate">
                        {mis.id}
                      </span>
                      <Badge variant="outline" className="text-[10px] shrink-0">
                        {mis.type} Misstatement
                      </Badge>
                      <Badge variant="outline" className="text-[10px] shrink-0">
                        Workspace: {mis.workspace || "N/A"}
                      </Badge>
                    </div>
                    <p className="text-xs text-white/50 mt-0.5">
                      Click to view details
                    </p>
                  </button>
                  {!misstatementSignoffLoading && (
                    <div className="flex items-center gap-4 flex-shrink-0">
                      {isArchivalMode ? (
                        <>
                          <div className="w-24 flex justify-center">
                            {signoff.prep_so?.length > 0 && (
                              <div className="flex items-center gap-0.5">
                                {signoff.prep_so.slice(0, 3).map((entry: any, i: number) => (
                                  <div
                                    key={i}
                                    className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                    title={entry.metadata}
                                  >
                                    {getInitialsForSignoff(entry.name)}
                                  </div>
                                ))}
                                {signoff.prep_so.length > 3 && (
                                  <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                    +{signoff.prep_so.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="w-24 flex justify-center">
                            {signoff.rev_so?.length > 0 && (
                              <div className="flex items-center gap-0.5">
                                {signoff.rev_so.slice(0, 3).map((entry: any, i: number) => (
                                  <div
                                    key={i}
                                    className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                    title={entry.metadata}
                                  >
                                    {getInitialsForSignoff(entry.name)}
                                  </div>
                                ))}
                                {signoff.rev_so.length > 3 && (
                                  <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                    +{signoff.rev_so.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="w-24 flex justify-center">
                            {signoff.conc_so?.length > 0 && (
                              <div className="flex items-center gap-0.5">
                                {signoff.conc_so.slice(0, 3).map((entry: any, i: number) => (
                                  <div
                                    key={i}
                                    className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                    title={entry.metadata}
                                  >
                                    {getInitialsForSignoff(entry.name)}
                                  </div>
                                ))}
                                {signoff.conc_so.length > 3 && (
                                  <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                    +{signoff.conc_so.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="w-24 flex justify-center">
                            {signoff.eqr_so?.length > 0 && (
                              <div className="flex items-center gap-0.5">
                                {signoff.eqr_so.slice(0, 3).map((entry: any, i: number) => (
                                  <div
                                    key={i}
                                    className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                    title={entry.metadata}
                                  >
                                    {getInitialsForSignoff(entry.name)}
                                  </div>
                                ))}
                                {signoff.eqr_so.length > 3 && (
                                  <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                    +{signoff.eqr_so.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="w-24 flex justify-center">
                            {signoff.partner_so?.length > 0 && (
                              <div className="flex items-center gap-0.5">
                                {signoff.partner_so.slice(0, 3).map((entry: any, i: number) => (
                                  <div
                                    key={i}
                                    className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                    title={entry.metadata}
                                  >
                                    {getInitialsForSignoff(entry.name)}
                                  </div>
                                ))}
                                {signoff.partner_so.length > 3 && (
                                  <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                    +{signoff.partner_so.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="w-24 flex justify-center">
                            <ControlSignoffBadge
                              label="Preparer"
                              signoffArray={signoff.prep_so || []}
                              onSignoffClick={() => handleMisstatementSignoff(mis.id, "prep_so")}
                              disabled={!canPrep}
                            />
                          </div>
                          <div className="w-24 flex justify-center">
                            <ControlSignoffBadge
                              label="Reviewer"
                              signoffArray={signoff.rev_so || []}
                              onSignoffClick={() => handleMisstatementSignoff(mis.id, "rev_so")}
                              disabled={!canRev}
                            />
                          </div>
                          <div className="w-24 flex justify-center">
                            <ControlSignoffBadge
                              label="Concurrent"
                              signoffArray={signoff.conc_so || []}
                              onSignoffClick={() => handleMisstatementSignoff(mis.id, "conc_so")}
                              disabled={!canConc}
                            />
                          </div>
                          <div className="w-24 flex justify-center">
                            <ControlSignoffBadge
                              label="EQR"
                              signoffArray={signoff.eqr_so || []}
                              onSignoffClick={() => handleMisstatementSignoff(mis.id, "eqr_so")}
                              disabled={!canEqr}
                            />
                          </div>
                          <div className="w-24 flex justify-center">
                            <ControlSignoffBadge
                              label="Partner"
                              signoffArray={signoff.partner_so || []}
                              onSignoffClick={() => handleMisstatementSignoff(mis.id, "partner_so")}
                              disabled={!canPartner}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  <div className="w-8 flex-shrink-0" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

